import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { config } from '../config'
import { queryClient } from '../lib/queryClient'
import { useAuth } from './AuthContext'
import { SocketContext, SocketContextType } from './SocketContext'
import { createInvalidationGate, REALTIME_EVENTS, RealtimePayload, realtimeInvalidationKeys } from '../lib/realtime'

/** Every realtime invalidation goes through here, so a drag in flight can hold them until the drop (ADR 0009). */
const realtimeGate = createInvalidationGate(queryKey => void queryClient.invalidateQueries({ queryKey }))

interface SocketProviderProps {
	children: ReactNode
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
	const [socket, setSocket] = useState<Socket | null>(null)
	const [isConnected, setIsConnected] = useState(false)
	const { token, user } = useAuth()

	useEffect(() => {
		if (token && user) {
			// Get the base URL without /api/v1 for Socket.IO
			const socketURL = config.apiUrl

			// Initialize Socket.IO connection
			const newSocket = io(socketURL, {
				auth: {
					token,
				},
				transports: ['websocket', 'polling'],
				autoConnect: true, // Explicitly enable auto-connect
			})

			// Connection event handlers
			newSocket.on('connect', () => {
				console.log('✅ Socket.IO connected:', newSocket.id)
				setIsConnected(true)
			})

			newSocket.on('disconnect', reason => {
				console.log('❌ Socket.IO disconnected:', reason)
				setIsConnected(false)
			})

			newSocket.on('connect_error', error => {
				console.error('🚨 Socket.IO connection error:', error)
				setIsConnected(false)
			})

			newSocket.io.on('reconnect', () => {
				console.log('🔄 Socket.IO reconnected, invalidating queries...')
				queryClient.invalidateQueries()
			})

			newSocket.on('connected', data => {
				console.log('✅ Socket.IO authenticated:', data)
			})

			newSocket.on('error', error => {
				console.error('🚨 Socket.IO error:', error)
			})

			// Realtime cache invalidation: subscribe once and invalidate the keys
			// each event maps to (see lib/realtime). react-query only refetches keys
			// with a mounted observer, so screens no longer carry their own handlers.
			REALTIME_EVENTS.forEach(event => {
				newSocket.on(event, (data: RealtimePayload) => {
					console.log(`📢 ${event}:`, data)
					for (const key of realtimeInvalidationKeys(event, data ?? {})) {
						realtimeGate.invalidate(key)
					}
				})
			})

			setSocket(newSocket)

			// Explicitly connect
			if (!newSocket.connected) {
				console.log('🔌 Manually connecting socket...')
				newSocket.connect()
			}

			// Cleanup on unmount
			return () => {
				console.log('🧹 Cleaning up socket connection')
				newSocket.close()
			}
		} else {
			// Logged out: tear down any existing socket without depending on `socket`
			setSocket(prev => {
				if (prev) {
					console.log('🚪 Logging out, closing socket')
					prev.close()
				}
				return null
			})
			setIsConnected(false)
		}
	}, [token, user])

	const joinTrip = useCallback(
		(tripId: string) => {
			if (socket && isConnected) {
				socket.emit('join_trip', { tripId })
				console.log(`🔗 Joined trip room: trip:${tripId}`)
			}
		},
		[socket, isConnected],
	)

	const leaveTrip = useCallback(
		(tripId: string) => {
			if (socket && isConnected) {
				socket.emit('leave_trip', { tripId })
				console.log(`🔌 Left trip room: trip:${tripId}`)
			}
		},
		[socket, isConnected],
	)

	const value: SocketContextType = useMemo(
		() => ({
			socket,
			isConnected,
			joinTrip,
			leaveTrip,
			holdRealtime: realtimeGate.hold,
		}),
		[socket, isConnected, joinTrip, leaveTrip],
	)

	return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}
